/**
 * Pure planning for "Copy → Screen". The flipbook is baked onto a screen as
 * ordinary elements — frames cannot nest (packages/element/src/frame.ts:454),
 * so page *contents* are copied and the page frames are not.
 *
 * ponytail: one bake per screen, keyed by screen id. Re-baking the same
 * screen replaces it. If a screen ever needs two flipbooks, this key grows a
 * book id alongside screenId.
 */
import {
  isElbowArrow,
  isFreeDrawElement,
  isLinearElement,
  isTextElement,
} from "@excalidraw/element";
import { pointFrom } from "@excalidraw/math";

import type {
  ExcalidrawElement,
  FixedSegment,
} from "@excalidraw/element/types";
import type { GlobalPoint, LocalPoint } from "@excalidraw/math";

export const FLIPBOOK_BAKE_KEY = "flipbookBake";

export type BakeTag = {
  screenId: string;
  pageIndex: number;
  /** which flipbook this copy came from — several books can be baked onto the
   * same screen, and each must re-bake without disturbing the others */
  bookId?: string;
  /** length of this book's full cycle, ms — the screen holds the longest one */
  cycleMs?: number;
};

export type Box = { x: number; y: number; width: number; height: number };

export const getBakeTag = (el: ExcalidrawElement): BakeTag | null => {
  const tag = el.customData?.[FLIPBOOK_BAKE_KEY];
  return typeof tag?.screenId === "string" &&
    typeof tag?.pageIndex === "number" &&
    Number.isInteger(tag.pageIndex) &&
    tag.pageIndex >= 0
    ? {
        screenId: tag.screenId,
        pageIndex: tag.pageIndex,
        bookId: typeof tag.bookId === "string" ? tag.bookId : undefined,
        cycleMs:
          typeof tag.cycleMs === "number" && tag.cycleMs > 0
            ? tag.cycleMs
            : undefined,
      }
    : null;
};

/**
 * Ids of a previous bake on `screenId` — deleted before a re-bake. Scoped to
 * one book when `bookId` is given, so re-baking flipbook 2 onto a screen keeps
 * flipbook 1's copies there. Bakes written before books existed carry no
 * bookId; they match the legacy book so they are still replaced rather than
 * duplicated.
 */
export const findBakedIds = (
  elements: readonly ExcalidrawElement[],
  screenId: string,
  bookId?: string,
  legacyBookId?: string,
): Set<string> => {
  const ids = new Set<string>();
  for (const el of elements) {
    const tag = getBakeTag(el);
    if (!tag || tag.screenId !== screenId) {
      continue;
    }
    const tagBook = tag.bookId ?? legacyBookId;
    if (bookId === undefined || tagBook === bookId) {
      ids.add(el.id);
    }
  }
  return ids;
};

/**
 * How long a screen must run to complete every flipbook baked onto it: the
 * longest cycle among its bakes, so a second, shorter book loops inside the
 * screen's duration instead of truncating the longer one.
 */
export const screenCycleMs = (
  elements: readonly ExcalidrawElement[],
  screenId: string,
  candidateMs: number,
): number => {
  let longest = candidateMs;
  for (const el of elements) {
    const tag = getBakeTag(el);
    if (tag?.screenId === screenId && tag.cycleMs) {
      longest = Math.max(longest, tag.cycleMs);
    }
  }
  return longest;
};

export const unionBox = (boxes: readonly Box[]): Box | null => {
  if (boxes.length === 0) {
    return null;
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const b of boxes) {
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.width);
    maxY = Math.max(maxY, b.y + b.height);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

/**
 * Uniform scale-to-fit with centring. Returns the target box the content
 * should occupy inside `screen`, plus the scale that gets it there.
 */
export const computeBakeLayout = (
  content: Box,
  screen: Box,
  padding = 0,
): { scale: number; width: number; height: number; x: number; y: number } => {
  const availW = Math.max(0, screen.width - padding * 2);
  const availH = Math.max(0, screen.height - padding * 2);
  const scale =
    content.width > 0 && content.height > 0
      ? Math.min(availW / content.width, availH / content.height)
      : 1;
  const width = content.width * scale;
  const height = content.height * scale;
  return {
    scale,
    width,
    height,
    x: screen.x + (screen.width - width) / 2,
    y: screen.y + (screen.height - height) / 2,
  };
};

/** `planScale`'s output: base geometry plus whichever type-specific fields
 * the element carries. A plain `Partial<ExcalidrawElement>` can't express
 * this — `keyof` a union only sees the members' shared keys. */
type ScalePlan = Partial<ExcalidrawElement> & {
  fontSize?: number;
  points?: readonly LocalPoint[];
  fixedSegments?: readonly FixedSegment[];
};

/**
 * Uniform scale of one element about `origin`, re-anchored so that `origin`
 * lands on `target`. Returns a mutateElement-shaped partial.
 *
 * ponytail: scales geometry, text size and linear points only. Stroke width
 * is deliberately left alone — the editor's own resize does not scale it
 * either. Bound-text containers and arrow bindings are not re-fitted; if a
 * baked flipbook ever needs those, route through the editor's resize path
 * instead of this function.
 */
export const planScale = (
  el: ExcalidrawElement,
  scale: number,
  origin: GlobalPoint,
  target: GlobalPoint,
): ScalePlan => {
  const base = {
    x: (el.x - origin[0]) * scale + target[0],
    y: (el.y - origin[1]) * scale + target[1],
    width: el.width * scale,
    height: el.height * scale,
  };

  if (isTextElement(el)) {
    return { ...base, fontSize: el.fontSize * scale };
  }

  if (isLinearElement(el) || isFreeDrawElement(el)) {
    const points = el.points.map(([px, py]) =>
      pointFrom<LocalPoint>(px * scale, py * scale),
    );

    if (isElbowArrow(el) && el.fixedSegments) {
      // Derive start/end from the rescaled points (same precedent as the
      // editor's own resize, packages/element/src/resizeElements.ts) rather
      // than scaling them independently, so a dragged mid-segment stays put
      // relative to its neighbours. Out-of-range index (hand-edited scene)
      // falls back to independent scaling instead of writing undefined.
      const fixedSegments = el.fixedSegments.map((segment) => {
        const start = points[segment.index - 1];
        const end = points[segment.index];
        return start && end
          ? { ...segment, start, end }
          : {
              ...segment,
              start: pointFrom<LocalPoint>(
                segment.start[0] * scale,
                segment.start[1] * scale,
              ),
              end: pointFrom<LocalPoint>(
                segment.end[0] * scale,
                segment.end[1] * scale,
              ),
            };
      });
      return { ...base, points, fixedSegments };
    }

    return { ...base, points };
  }

  return base;
};
